<template>
  <div ref="uploadContainer" class="upload-container-1">
    <!-- Existing / Dummy Image Preview -->
    <div class="preview">
      
      <img
        :src="previewImage"
        :alt="'Profile Photo'"
        :width="size"
        :height="size"
        class="border"
      />
      

      <!-- Upload Button -->
      <div v-if="photoEditable != 0" class="actions mt-3">
        <b-button variant="primary" @click="$bvModal.show('uploadModal')">
          <i class="fa fa-upload"></i> {{ buttonLabel }}
        </b-button>
      </div>
    </div>

    <!-- BootstrapVue Modal -->
    <b-modal
      id="uploadModal"
      title="Upload Profile Photo"
      hide-footer
      centered
      @hidden="resetModal"
    >
      <ul class="instructions">
        <li>📏 Image dimension must be exactly <b>{{ requiredWidth }}x{{ requiredHeight }} px</b></li>
        <li>🧾 Maximum file size: <b>{{ maxSizeKB }} KB</b></li>
        <li>🖼️ Accepted formats: <b>JPEG / PNG</b></li>
        <li>🎨 For best results, use a clear, passport-style photo with a plain background.</li>
      </ul>

      <!-- File Input -->
      <div class="file-input mt-3 text-center">
        <label class="upload-label btn btn-outline-primary" for="fileInput">
          <i class="fa fa-image"></i> Choose Image
        </label>
        <input
          id="fileInput"
          ref="fileInput"
          type="file"
          accept="image/*"
          @change="onFileChange"
          hidden
        />
      </div>

      <!-- Preview -->
      <div v-if="selectedPreview" class="preview mt-3 text-center">
        <img
          :src="selectedPreview"
          :alt="'Selected Image'"
          width="150"
          height="150"
          class="border"
        />
      </div>

      <!-- Error -->
      <p v-if="errorMessage" class="text-danger mt-2 text-center">{{ errorMessage }}</p>

      <!-- Actions -->
      <div class="actions mt-4 d-flex justify-content-center gap-2">
        <b-button
          variant="primary"
          @click="uploadImage"
          :disabled="!selectedFile || isUploading"
        >
          <i class="fa fa-upload"></i>
          {{ isUploading ? 'Uploading...' : 'Upload' }}
        </b-button>
        <b-button variant="secondary btn btn-sm" @click="$bvModal.hide('uploadModal')">Cancel</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { POST } from "@/utils/Request";
import { UPLOAD_PHOTO } from "@/const/api";

export default {
  name: "ImageUploadModal",
  props: {
    size: { type: Number, default: 300 },
    existingImage: { type: String, default: "" },
    buttonLabel: { type: String, default: "Upload Photo" },
    maxSizeKB: { type: Number, default: 200 },
    requiredWidth: { type: Number, default: 300 },
    requiredHeight: { type: Number, default: 300 },
    photoEditable: { type: Number, default: 0 },
  },
  data() {
    return {
      selectedFile: null,
      selectedPreview: null,
      errorMessage: "",
      isUploading: false,
      cacheBuster: Date.now(),
    };
  },
  watch: {
    existingImage(newVal) {
      this.cacheBuster = Date.now();
    },
  },
  computed: {
    previewImage() {
      return (
        this.existingImage
      ? `${this.existingImage}?v=${this.cacheBuster}`
      :  "https://via.placeholder.com/300x300.png?text=No+Photo"
      );
    },
  },
  methods: {
    resetModal() {
      this.selectedFile = null;
      this.selectedPreview = null;
      this.errorMessage = "";
      this.isUploading = false;
    },
    async onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;

      // if (!file.type.startsWith("image/")) {
      //   this.errorMessage = "Please select a valid image file!";
      //   return;
      // }

      // if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      //   this.errorMessage = "Only PNG or JPEG images are allowed!";
      //   return;
      // }

      // if (file.size / 1024 > this.maxSizeKB) {
      //   this.errorMessage = `File too large! Max allowed size is ${this.maxSizeKB} KB.`;
      //   return;
      // }

      // const { width, height } = await this.getImageDimensions(file);
      // if (width !== this.requiredWidth || height !== this.requiredHeight) {
      //   this.errorMessage = `Image must be exactly ${this.requiredWidth}x${this.requiredHeight}px.`;
      //   return;
      // }

      // 1️⃣ Validate type first
        const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
        if (!allowedTypes.includes(file.type)) {
          this.errorMessage = "Invalid file type. Only PNG or JPEG images are allowed!";
          this.selectedFile = null;
          this.selectedPreview = null;
          return;
        }

        // 2️⃣ Validate file size
        const maxKB = this.maxSizeKB;
        if (file.size / 1024 > maxKB) {
          this.errorMessage = `File too large! Maximum allowed size is ${maxKB} KB.`;
          this.selectedFile = null;
          this.selectedPreview = null;
          return;
        }

        // 3️⃣ Validate image dimensions
        try {
          const { width, height } = await this.getImageDimensions(file);
          if (width !== this.requiredWidth || height !== this.requiredHeight) {
            this.errorMessage = `Image must be exactly ${this.requiredWidth}x${this.requiredHeight}px. Your image is ${width}x${height}px.`;
            this.selectedFile = null;
            this.selectedPreview = null;
            return;
          }
        } catch (err) {
          this.errorMessage = "Failed to read image dimensions. Please try another image.";
          this.selectedFile = null;
          this.selectedPreview = null;
          return;
        }

      // ✅ If all checks pass
      this.errorMessage = "";
      this.selectedFile = file;
      this.selectedPreview = URL.createObjectURL(file);
    },
    getImageDimensions(file) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });
    },
    async uploadImage() {
      if (!this.selectedFile) {
        this.errorMessage = "Please select an image before uploading.";
        return;
      }

      const formData = new FormData();
      formData.append("photo", this.selectedFile);

      this.isUploading = true;
      try {
        const response = await POST(UPLOAD_PHOTO, formData);
        if (response.status === 200) {
          const newUrl = response.data.url;
          this.$emit("uploaded", {"status":response.data.status,"url":newUrl});
          this.selectedPreview = newUrl;
          this.cacheBuster = Date.now();
          this.$iziToast.success({ message: "Photo Uploaded Successfully!" });
          this.$bvModal.hide("uploadModal");
        } else {
          this.errorMessage =
            response.message || "Upload failed. Please try again.";
        }
      } catch (error) {
        console.error(error);
        this.errorMessage = "Upload failed. Please try again.";
      } finally {
        this.isUploading = false;
      }
    },
  },
};
</script>

<style scoped>
.upload-container-1 {
  display: flex !important;
  flex-direction: row;
  justify-content: center;
}

.preview img {
  /*border-radius: 50%;*/
  border: 1px solid #ddd;
  object-fit: cover;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2) !important;
}
.preview img:hover{
  transform: scale(1.05);
}


.actions {
  display: flex;
  /* gap: 10px; */
  /* justify-content: center; */
}

.instructions {
  font-size: 0.95rem;
  line-height: 1.4;
  color: #444;
  background: #f8f8ff;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  margin-bottom: 15px;
  list-style-type: none;
}
.upload-label {
  cursor: pointer;
}
</style>
